import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import FormField from '../../FormField/FormField';
import { useDebounce } from '../../../hooks/useDebounce';
import { useValidateEmailMutation } from '../../../store/services/addSellerService';

interface Props {
  disabled?: boolean;
  dataTestId?: string;
  skipInitialValidation?: boolean;
}

export default function DebouncedEmail({
  disabled,
  dataTestId,
  skipInitialValidation,
}: Props) {
  const { t } = useTranslation();
  const { watch, setError, clearErrors } = useFormContext();
  const [isValid, setIsValid] = useState(true);

  const value = watch('email');
  const debouncedValue = useDebounce(value, 500);
  const lastCheckedRef = useRef<string | null>(null);

  const [validateEmail, { isLoading }] = useValidateEmailMutation();

  const requiredError = t('components.validations.requiredField');

  const emailSchema = z
    .string({ message: requiredError })
    .email({ message: t('components.validations.invalidEmail') });

  const setEmailError = (message: string) => {
    setError('email', { type: 'validate', message });
  };

  const isEmailFormatValid = (): boolean => {
    const { success } = emailSchema.safeParse(debouncedValue);
    if (!success) {
      setEmailError(t('components.validations.invalidEmail'));
      return false;
    }
    return true;
  };

  const checkEmailUniqueness = async () => {
    const isFormatValid = isEmailFormatValid();
    if (!isFormatValid) return;

    try {
      await validateEmail({ email: debouncedValue }).unwrap();
      setIsValid(true);
      clearErrors('email');
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      setIsValid(false);
      setEmailError(error?.data?.message || t('components.validations.invalidEmail'));
    }
  };

  useEffect(() => {
    if (skipInitialValidation) return;
    if (!debouncedValue) return;
    if (lastCheckedRef.current === debouncedValue) return;
    lastCheckedRef.current = debouncedValue;

    checkEmailUniqueness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, skipInitialValidation]);

  return (
    <FormField
      id="email"
      name="email"
      type="text"
      label={t('sellers.addSeller.email')}
      placeholder={t('sellers.addSeller.emailPlaceholder')}
      data-testid={dataTestId || 'add-seller-email'}
      disabled={disabled || isLoading}
      // @ts-expect-error onBlur event
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
        const val = e.target.value?.trim();
        if (!val) {
          setEmailError(requiredError);
          return;
        }
        const isFormatValid = isEmailFormatValid();
        if (!isFormatValid) return;
        if (!isValid) {
          setEmailError(t('components.validations.invalidEmail'));
        }
      }}
    />
  );
}
